import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { WalletReadyState } from '@solana/wallet-adapter-base';
import { useWallet } from '../../contexts/WalletContext';
import { theme, commonStyles } from '../../styles/theme';

const WalletModal = ({ open, onClose, selectedNetwork = 'solana' }) => {
  const {
    connect,
    connecting,
    availableSolanaWallets,
    availableEVMWallets,
    availableMultiProtocolWallets,
    connectMultiProtocolWallet,
    solanaWallet
  } = useWallet();

  const [error, setError] = useState(null);

  const handleConnect = async (type) => {
    setError(null);
    try {
      // Pass the selected network to the connect function
      await connect(type, selectedNetwork);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to connect wallet');
    }
  };

  const handleSolanaWalletSelect = async (wallet) => {
    setError(null);

    try {
      if (!wallet?.adapter) {
        throw new Error('Wallet adapter not available. Please reinstall or refresh.');
      }

      const adapter = wallet.adapter;
      const adapterName = adapter.name;
      console.log('🔗 Attempting to connect to:', adapterName);
      console.log('Adapter details:', {
        name: adapterName,
        readyState: adapter.readyState,
        connected: adapter.connected
      });

      if (adapter.readyState === WalletReadyState.NotDetected) {
        throw new Error(`${adapterName} is not detected. Ensure the extension is installed and unlocked.`);
      }

      if (adapter.readyState === WalletReadyState.Unsupported) {
        throw new Error(`${adapterName} is not supported in this environment.`);
      }

      // IMPORTANT: Select the wallet by name first - this tells the wallet provider
      // which adapter to use. The select() function is synchronous and updates
      // the internal state, but the React context won't reflect this immediately.
      console.log('📍 Selecting wallet adapter:', adapterName);
      solanaWallet.select(adapterName);
      
      // Small delay to allow the wallet provider to process the selection
      await new Promise(resolve => setTimeout(resolve, 50));

      // Now connect using the adapter directly to avoid stale closure issues
      // This ensures we connect to the correct wallet (Nightly, not Phantom)
      console.log('🔌 Connecting directly to adapter:', adapterName);
      
      if (!adapter.connected) {
        await adapter.connect();
      }

      console.log('✅ Connection successful!', {
        adapterName: adapter.name,
        connected: adapter.connected,
        publicKey: adapter.publicKey?.toString()
      });

      // Close modal - the context will pick up the connection state
      onClose();
    } catch (err) {
      console.error('❌ Solana wallet connection error:', err);
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
      
      // Ignore WalletNotSelectedError - it's a transient state during connection
      if (err.name === 'WalletNotSelectedError' || err.message?.includes('WalletNotSelectedError')) {
        console.log('ℹ️ WalletNotSelectedError is transient, ignoring...');
        onClose(); // Close modal, wallet will connect via context
        return;
      }
      
      // Provide more helpful error messages
      if (err.message?.includes('User rejected') || err.message?.includes('rejected the request')) {
        setError('Connection cancelled. Please approve the connection in your wallet.');
      } else if (err.message?.includes('WalletNotReadyError')) {
        setError('Wallet is not ready. Please unlock your wallet and try again.');
      } else {
        setError(err.message || 'Failed to connect wallet. Please try again.');
      }
    }
  };

  const handleMultiProtocolConnect = async (walletKey, protocol) => {
    setError(null);
    try {
      await connectMultiProtocolWallet(walletKey, protocol);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to connect wallet');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0a0a0a',
          backgroundImage: 'linear-gradient(135deg, rgba(220,38,38,0.05) 0%, rgba(0,0,0,0.95) 100%)',
          border: `1px solid ${theme.colors.borderPrimary}`,
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(39, 48, 63, 0.5)',
        pb: 2,
        color: '#fff',
        fontWeight: 700
      }}>
        Connect Wallet
        <IconButton onClick={onClose} sx={{ color: theme.colors.textSecondary, '&:hover': { color: theme.colors.primary } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Show wallets based on selected network */}
        {selectedNetwork === 'solana' ? (
          <>
            {/* Solana Wallets */}
            {availableSolanaWallets.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" sx={{ color: '#14F195', mb: 2, fontWeight: 600, fontSize: '0.875rem' }}>
                  Select a Solana Wallet:
                </Typography>
                <Stack spacing={1.5}>
                  {availableSolanaWallets.map((wallet) => (
                    <Button
                      key={wallet.adapter.name}
                      variant="outlined"
                      fullWidth
                      disabled={connecting}
                      onClick={() => handleSolanaWalletSelect(wallet)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderColor: 'rgba(20, 241, 149, 0.3)',
                        color: '#14F195',
                        justifyContent: 'flex-start',
                        bgcolor: theme.colors.bgGlass,
                        '&:hover': {
                          borderColor: '#14F195',
                          bgcolor: 'rgba(20, 241, 149, 0.1)',
                          transform: 'translateX(4px)',
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ 
                          width: 36, 
                          height: 36, 
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(20, 241, 149, 0.15)',
                          borderRadius: 1,
                          border: '1px solid rgba(20, 241, 149, 0.3)'
                        }}>
                          {wallet.adapter.icon ? (
                            <img 
                              src={wallet.adapter.icon} 
                              alt={wallet.adapter.name}
                              style={{ width: 24, height: 24, borderRadius: 4 }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                          ) : null}
                          <Typography sx={{ display: wallet.adapter.icon ? 'none' : 'block', fontSize: '1.2rem' }}>
                            ◎
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {wallet.adapter.name}
                          </Typography>
                        </Box>
                        {connecting && <CircularProgress size={18} sx={{ color: '#94a3b8' }} />}
                      </Box>
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}
          </>
        ) : selectedNetwork === 'ethereum' ? (
          <>
            {/* Ethereum Wallets */}
            {availableEVMWallets.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" sx={{ color: '#627EEA', mb: 2 }}>
                  Select an Ethereum Wallet:
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={connecting}
                  onClick={() => handleConnect('ethereum')}
                  sx={{
                    py: 2,
                    borderColor: 'rgba(98, 126, 234, 0.5)',
                    color: '#627EEA',
                    justifyContent: 'flex-start',
                    '&:hover': {
                      borderColor: '#627EEA',
                      bgcolor: 'rgba(98, 126, 234, 0.1)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography variant="h5" sx={{ mr: 2 }}>Ξ</Typography>
                    <Box sx={{ flex: 1, textAlign: 'left' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Connect Ethereum Wallet
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(98, 126, 234, 0.7)' }}>
                        {availableEVMWallets.map(w => w.name).join(', ')}
                      </Typography>
                    </Box>
                    {connecting && <CircularProgress size={20} sx={{ color: '#627EEA' }} />}
                  </Box>
                </Button>
              </Box>
            )}
          </>
        ) : (
          <>
            {/* Abstract Wallets */}
            {availableEVMWallets.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" sx={{ color: '#FF6B00', mb: 2 }}>
                  Select an Abstract Wallet:
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={connecting}
                  onClick={() => handleConnect('ethereum')}
                  sx={{
                    py: 2,
                    borderColor: 'rgba(255, 107, 0, 0.5)',
                    color: '#FF6B00',
                    justifyContent: 'flex-start',
                    '&:hover': {
                      borderColor: '#FF6B00',
                      bgcolor: 'rgba(255, 107, 0, 0.1)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography variant="h5" sx={{ mr: 2 }}>A</Typography>
                    <Box sx={{ flex: 1, textAlign: 'left' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        Connect Abstract Wallet
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 107, 0, 0.7)' }}>
                        {availableEVMWallets.map(w => w.name).join(', ')}
                      </Typography>
                    </Box>
                    {connecting && <CircularProgress size={20} sx={{ color: '#FF6B00' }} />}
                  </Box>
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Multi-Protocol Wallets - Show for all networks */}
        {availableMultiProtocolWallets.length > 0 && (
          <Box mb={3}>
            <Typography variant="subtitle2" sx={{ color: theme.colors.primary, mb: 2, fontWeight: 600, fontSize: '0.875rem' }}>
              Multi-Protocol Wallets
            </Typography>
            <Stack spacing={1.5}>
              {availableMultiProtocolWallets.map((wallet) => (
                <Box key={wallet.key} sx={{ 
                  p: 2, 
                  border: `1px solid ${theme.colors.borderPrimary}`,
                  borderRadius: 1,
                  bgcolor: theme.colors.bgGlass
                }}>
                  <Typography variant="body2" sx={{ mb: 1.5, color: '#fff', fontWeight: 600 }}>
                    {wallet.icon} {wallet.name}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={connecting}
                      onClick={() => handleMultiProtocolConnect(wallet.key, 'solana')}
                      sx={{
                        py: 1,
                        borderColor: 'rgba(20, 241, 149, 0.3)',
                        color: '#14F195',
                        bgcolor: theme.colors.bgGlass,
                        '&:hover': {
                          borderColor: '#14F195',
                          bgcolor: 'rgba(20, 241, 149, 0.1)',
                        }
                      }}
                    >
                      ◎ Solana
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={connecting}
                      onClick={() => handleMultiProtocolConnect(wallet.key, 'ethereum')}
                      sx={{
                        py: 1,
                        borderColor: 'rgba(98, 126, 234, 0.3)',
                        color: '#627EEA',
                        bgcolor: theme.colors.bgGlass,
                        '&:hover': {
                          borderColor: '#627EEA',
                          bgcolor: 'rgba(98, 126, 234, 0.1)',
                        }
                      }}
                    >
                      Ξ Ethereum
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}


        {/* No Wallets */}
        {availableSolanaWallets.length === 0 && 
         availableEVMWallets.length === 0 && 
         availableMultiProtocolWallets.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              No wallets detected. Please install a wallet extension.
            </Typography>
            <Stack spacing={2}>
              <Button
                variant="outlined"
                href="https://phantom.app/"
                target="_blank"
                sx={{ borderColor: '#14F195', color: '#94a3b8' }}
              >
                Get Phantom (Solana)
              </Button>
              <Button
                variant="outlined"
                href="https://metamask.io/"
                target="_blank"
                sx={{ borderColor: '#627EEA', color: '#627EEA' }}
              >
                Get MetaMask (Ethereum)
              </Button>
            </Stack>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WalletModal;
